import { AlertTriangle, Loader2, Send } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bookdental/ui";
import { formatBRL } from "./prosthesis-meta";
import type { ServiceDraft } from "./prosthesis-service-draft";
import { totalParaExibir } from "@/components/prosthesis/valor-do-pedido";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  patientName: string;
  labName: string | null;
  services: ServiceDraft[];
  deadline: string;
  description: string;
  isUrgent: boolean;
  sentChannels: string[];
};

/** Tela de confirmação antes de enviar o pedido de verdade — última chance de revisar
 *  paciente, laboratório, serviços, prazo e observações. */
export function ConfirmSendOrderDialog({
  open,
  onClose,
  onConfirm,
  isPending,
  patientName,
  labName,
  services,
  deadline,
  description,
  isUrgent,
  sentChannels,
}: Props) {
  const totalCents = totalParaExibir(services);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !isPending && onClose()}>
      <DialogContent className="max-w-lg dark:bg-card">
        <DialogHeader>
          <DialogTitle>Confirmar envio do pedido</DialogTitle>
          <DialogDescription>Revise os dados antes de enviar — depois disso o laboratório já recebe o pedido.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 rounded-lg bg-zinc-50 p-3 dark:bg-background/50">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Paciente</p>
              <p className="font-medium text-zinc-900 dark:text-foreground">{patientName}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Laboratório</p>
              <p className="font-medium text-zinc-900 dark:text-foreground">{labName ?? "Nenhum (a definir depois)"}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Prazo</p>
              <p className="flex items-center gap-1.5 font-medium text-zinc-900 dark:text-foreground">
                {deadline ? deadline.split("-").reverse().join("/") : "—"}
                {isUrgent && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800 dark:bg-amber-500/15 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3" />
                    Urgente
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Valor total</p>
              <p className="font-medium text-zinc-900 dark:text-foreground">{formatBRL(totalCents)}</p>
            </div>
          </div>

          <div>
            <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
              Serviços ({services.length})
            </p>
            <ul className="space-y-1">
              {services.map((s, i) => (
                <li key={i} className="rounded-md border border-zinc-100 px-2.5 py-1.5 dark:border-border">
                  <p className="font-medium text-zinc-900 dark:text-foreground">
                    {s.serviceCategoryLabel}
                    {s.teeth && <span className="ml-1 font-normal text-zinc-500 dark:text-muted-foreground">· {s.teeth}</span>}
                  </p>
                  {(s.materialOptionLabel || s.shadeOptionLabel) && (
                    <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                      {[s.materialOptionLabel, s.shadeOptionLabel].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {description && (
            <div>
              <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                Observações
              </p>
              <p className="text-zinc-700 dark:text-foreground/80">{description}</p>
            </div>
          )}

          {sentChannels.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                Enviado também por
              </p>
              <div className="flex flex-wrap gap-1.5">
                {sentChannels.map((channel) => (
                  <span
                    key={channel}
                    className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-700 dark:bg-muted dark:text-foreground/80"
                  >
                    {channel}
                  </span>
                ))}
              </div>
            </div>
          )}

          {isUrgent && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] leading-relaxed text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Esse prazo é mais curto que o previsto pelo laboratório — o pedido será avaliado como{" "}
                <strong>urgente</strong> e pode ser cobrada uma taxa extra pelo laboratório.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={isPending} onClick={onClose}>
            Voltar
          </Button>
          <Button type="button" disabled={isPending} onClick={onConfirm}>
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Confirmar e enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
